/**
 * MyTasksPanel — my open project tasks (assigned to or created by me), surfacing
 * the soonest-due first so the most urgent work is at the top. Self-scoped via
 * the task-portal `getMyTasks()` (cross-domain task data, per the dashboard
 * data-source spec). Overdue rows are flagged red.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Badge,
  Button,
  Card,
  Group,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@/components/ui';
import { TableScrollContainer } from '@mantine/core';
import { IconChecklist, IconArrowRight } from '@tabler/icons-react';
import { useTaskPortalTaskService, type TaskModelView } from '@actschurch/ccms-sdk';
import { TASKS_ROUTES } from '@/config/routes';
import { parseBackendDate } from '../../utils/projectHelpers';
import { MEMBER_DASHBOARD_KEY, OPEN_TASK_STATUS_IDS, MY_TASKS_FILTER } from './dashboardConstants';

function taskDueValue(t: TaskModelView): number {
  const due = parseBackendDate(t.dueDate ?? t.endDate);
  return due ? due.getTime() : Number.MAX_SAFE_INTEGER;
}

export function MyTasksPanel() {
  const navigate = useNavigate();
  const taskService = useTaskPortalTaskService();

  const { data: myTasks = [], isLoading, error } = useQuery({
    queryKey: [...MEMBER_DASHBOARD_KEY, 'my-tasks'],
    queryFn: () => taskService.getMyTasks(MY_TASKS_FILTER),
  });

  const openSoonest = useMemo(() => {
    return (myTasks as TaskModelView[])
      .filter((t) => OPEN_TASK_STATUS_IDS.has(t.statusID))
      .sort((a, b) => taskDueValue(a) - taskDueValue(b))
      .slice(0, 5);
  }, [myTasks]);

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <ThemeIcon size="md" variant="light" color="blue" radius="xl">
            <IconChecklist size={16} />
          </ThemeIcon>
          <Title order={5}>My Open Tasks</Title>
        </Group>
        <Button
          variant="subtle"
          size="xs"
          rightSection={<IconArrowRight size={14} />}
          onClick={() => navigate(TASKS_ROUTES.BOARD)}
        >
          View all
        </Button>
      </Group>

      {isLoading ? (
        <Stack gap="xs">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} height={28} />
          ))}
        </Stack>
      ) : error ? (
        <Text size="sm" c="red" ta="center" py="md">
          Could not load your tasks: {error instanceof Error ? error.message : 'service unavailable'}
        </Text>
      ) : openSoonest.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No open tasks — you're all caught up.
        </Text>
      ) : (
        <TableScrollContainer minWidth={360}>
        <Table tableVariant="striped">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Task</Table.Th>
              <Table.Th>Project</Table.Th>
              <Table.Th>Due</Table.Th>
              <Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {openSoonest.map((t) => {
              const due = parseBackendDate(t.dueDate ?? t.endDate);
              const overdue = !!(t.isOverDue ?? t.isOverdue);
              return (
                <Table.Tr
                  key={t.taskID}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(TASKS_ROUTES.DETAIL(String(t.taskID)))}
                >
                  <Table.Td>
                    <Text size="sm" fw={500} truncate>
                      {t.title || `Task ${t.taskID}`}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed" truncate>
                      {t.projectName || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c={overdue ? 'red' : undefined} fw={overdue ? 600 : undefined}>
                      {due ? dayjs(due).format('DD MMM') : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="sm"
                      variant="light"
                      color={overdue ? 'red' : t.statusColor ? undefined : 'gray'}
                    >
                      {overdue ? 'Overdue' : t.statusName ?? `Status ${t.statusID}`}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        </TableScrollContainer>
      )}
    </Card>
  );
}
